<template>
  <SectionContentLayout
    :state='kids_controller.state.events["fetch_all_definitions"]'
    content_class="kids-definitions">

    <template #close>
      <ActionButton @click='onClose()' class="close" icon="fas fa-times-circle" />
    </template>

    <template #header>
      <ContentTitle title="settings.kids.title" />
    </template>

    <template #content>
      <form v-if="Object.keys(kids_entity).length">
        <h3>{{ Language.say('settings.kids.form_fields.definitions.authorized_emails.title') }}</h3>
        <p>{{ Language.say('settings.kids.form_fields.definitions.authorized_emails.description') }}</p>

        <fieldset>
          <legend>{{ Language.say('settings.kids.form_fields.definitions.authorized_emails.radio_button.description') }}
          </legend>
          <InputRadio
            input_id="field_authorized_emails"
            input_name="authorized_emails"
            tag_text="settings.kids.form_fields.definitions.authorized_emails.radio_button.options."
            :options="authorized_emails_options"
            v-model:input_value="kids_entity.whitelist_active"
            @change="onUpdatePrefs">
          </InputRadio>
        </fieldset>

        <fieldset>
          <legend>{{ Language.say('settings.kids.form_fields.definitions.not_authorized_emails.radio_button.description')
          }}
          </legend>
          <InputRadio
            input_id="field_authorized_action"
            input_name="authorized_action"
            tag_text="settings.kids.form_fields.definitions.not_authorized_emails.radio_button.options."
            :options="authorized_action_options"
            v-model:input_value="kids_entity.authorized_action_emails"
            @change="onUpdatePrefs">
          </InputRadio>
        </fieldset>
      </form>

      <form @submit.prevent="onAddEmailWhitelist">
        <fieldset>
          <InputText
            input_id="field_recipient_email_input"
            input_name="recipient_email_input"
            label_text="settings.kids.form_fields.whitelist.label_input"
            :input_invalids="properties_validation.invalid"
            error_text="global.forms.invalid.email"
            input_placeholder="email.autorizado@sapo.pt"
            v-model:input_value="recipient_email_input">
          </InputText>
          <ActionButton
            class="main-action bottom-space"
            icon="fas fa-plus-circle"
            :loading="kids_controller.state.events['saving_whitelist_email'] === 'loading'"
            :text="Language.say('settings.kids.form_fields.whitelist.button_actions.submit')"
            @click="onAddEmailWhitelist()" />
        </fieldset>

        <fieldset>
          <h3>{{ Language.say('settings.kids.form_fields.whitelist.title') }}</h3>
          <p>{{ Language.say('settings.kids.form_fields.whitelist.description') }}</p>

          <ul class="emails-list">
            <SmartList
              @smartListItemsSet=onSmartListItemsSet
              :smart_list_items="kids_controller.data.whitelist.items">
              <template #elements>
                <template v-for="(whitelist) in smart_list_items" :key="whitelist.email">
                  <li class="item">
                    <span>
                      {{ whitelist.email }}
                    </span>
                    <ActionButton
                      @click="onRemoveWhitelist(whitelist.email)"
                      class="clear"
                      icon="fas fa-trash"
                      :loading="kids_controller.state.events[whitelist.email] == 'loading'" />
                  </li>
                </template>
              </template>
            </SmartList>
          </ul>
        </fieldset>

      </form>
    </template>
  </SectionContentLayout>
</template>

<script setup>
import { reactive, onBeforeMount, ref, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import ActionButton from '@/vue/Elements/Buttons/ActionButton.vue';
import SectionContentLayout from '@/vue/Layouts/Sections/SectionContentLayout.vue';
import Language from '@/js/Shared/Language/Language.js';
import SmartList from '@/vue/Elements/Tools/SmartList.vue';
import KidsController from '@/js/Settings/Kids/KidsController';
import InputRadio from '@/vue/Elements/Inputs/InputRadio.vue';
import InputText from '@/vue/Elements/Inputs/InputText.vue';
import ContentTitle from '@/vue/Elements/Content/ContentTitle.vue';
import PropertiesValidation from '@/js/Shared/Validation/PropertiesValidation';

const kids_controller = KidsController;
const smart_list_items = ref();
const kids_entity = ref({});
const recipient_email_input = ref('');
const router = useRouter();
const authorized_emails_options = {
  'active': 1,
  'not_active': 0
}
const authorized_action_options = {
  'folder': 'FOLDER',
  'reject': 'REJECT'
}
const properties_validation = reactive(new PropertiesValidation({
  recipient_email_input : {
    regex : 'email',
    required: true
  },
}));

onBeforeMount(async () => {
  await fetchAllDefinitions();
  kids_controller.state.on("fetch_all_definitions", "success", onLoad)
});

onUnmounted(() => {
  kids_controller.state.destroy("fetch_all_definitions", "success", onLoad)
});

const onLoad = () => {
  onSmartListItemsSet(kids_controller.data.whitelist.items);
  onSetKidsEntity(kids_controller.data)
}

const fetchAllDefinitions = async () => {
  await kids_controller.fetchAllDefinitions()
}

const onSetKidsEntity = (data) => {
  kids_entity.value = data;
}

const onAddEmailWhitelist = async () => {
  if (await addEmailToWhitelist() === false) {
    return;
  }

  setRecipientEmailValue('');
}

const addEmailToWhitelist = async () => {
  if (properties_validation.validateField('recipient_email_input', recipient_email_input.value) === false) {
    scrollIntoRef('field_recipient_email_input');
    return false;
  }

  let success = await kids_controller.addToWhitelist(recipient_email_input.value);

  return success;
}

const onUpdatePrefs = async () => {
  await nextTick();
  await kids_controller.updateDefinitions(kids_entity.value.authorized_action_emails,
    kids_entity.value.whitelist_active);
}

const onRemoveWhitelist = async (sender) => {
  await kids_controller.removeWhitelistSender(sender);
}

const onSmartListItemsSet = (items) => {
  setSmartListItems(items);
}

const setSmartListItems = (items) => {
  smart_list_items.value = items;
}

const onClose = () => {
  router.push('/settings');
}

const setRecipientEmailValue = (value) => {
  recipient_email_input.value = value;
}

const scrollIntoRef = (id_ref) => {
  let $element = document.getElementById(id_ref)

  $element.scrollIntoView({block: "end", behavior: "smooth"});
}
</script>
